import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Link, Paper, Popper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import VersionHistory from 'src/components/common/VersionHistory';
import { archLevel } from 'src/data/config';
import { setMessage } from 'src/store/Message';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setSelectedEMRId } from 'src/store/selectedEMR';
import EMREvaluationQuestionnaire from './EMREvaluationQuestionnaire';
import { useEMREvaluation } from './useEMREvalustion';
import { DataTable } from '@pihealth/common-ui-library';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { GridColumns, GridRenderCellParams, GridToolbarExport } from '@mui/x-data-grid-pro';
import moment from 'moment';
interface GridCellExpandProps {
  value: string;
  width: number;
}

function isOverflown(element: Element): boolean {
  return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
  const { width, value } = props;
  const wrapper = React.useRef<HTMLDivElement | null>(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current!);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent: KeyboardEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: 'center',
        lineHeight: '24px',
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex'
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: '100%',
          width,
          display: 'block',
          position: 'absolute',
          top: 0
        }}
      />
      <Box ref={cellValue} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </Box>
      {showPopper && (
        <Popper open={showFullCell && anchorEl !== null} anchorEl={anchorEl} style={{ width, marginLeft: -17 }}>
          <Paper elevation={6} style={{ minHeight: wrapper.current!.offsetHeight - 3 }}>
            <Typography variant="body1" style={{ padding: 12, wordBreak: 'break-word' }}>
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});

function renderCellExpand(params: GridRenderCellParams<string>) {
  return <GridCellExpand value={params.value || ''} width={params.colDef.computedWidth} />;
}

const DataGridEMREvaluation: React.FunctionComponent<any> = (props: any) => {
  const { setEMREvaluationOpen, setStudyLogOpen } = props;
  const { userPreferedDateFormat, getPreferredDateTimeInLocal } = usePreferedDateFormat();
  const isHierarchyLevelClosed: boolean = useAppSelector(state => state.site.isHierarchyLevelClosed);
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const [headerTitle, setHedertitle] = useState('');
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [docId, setDocId] = useState('');
  const dispatch = useAppDispatch();
  const [pageSize, setPageSize] = useState(10);
  const [rowsCount, setRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [openEMRevaluation, setOpenEMRevaluation] = useState(false);
  const [viewEMRGrid, setEMRGrid] = useState(true);
  const [rows, setRows] = useState<any>([]);
  const [selectedEMRRow, setselectedEMRRow] = useState<any>(null);
  const [filterObject, setFilterObject] = useState<any>();
  const currentHierarchyLevel = useAppSelector(state => state.site.currentHierarchyLevel);
  const { getAllEMREvaluation, getAllEMREvaluationData, getAllEMREvaluationIsLoading, getAllEMREvaluationIsSuccess } =
    useEMREvaluation();
  const formatDate = (params: any) => {
    return getPreferredDateTimeInLocal(params.value);
  };

  const columns: GridColumns = [
    {
      field: 'heading',
      headerName: 'Name',
      minWidth: 220,
      flex: 1,
      renderCell: (params: any) => (
        <Link onClick={field => handleClickEmREvalution(params.row)}>{params.value?.toString()}</Link>
      )
    },
    {
      field: 'site_id_value',
      headerName: 'Site',
      minWidth: 120,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'created_by',
      headerName: 'Initial Entry By',
      minWidth: 120,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'updated_by',
      headerName: 'Modified By',
      minWidth: 180,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'sub_type',
      headerName: 'Visit Type',
      minWidth: 140,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'start_date',
      headerName: 'Date of Visit',
      width: 140,
      type: 'dateTime',
      valueGetter: formatDate,
      renderCell: renderCellExpand
    },
    {
      field: 'updated_on',
      headerName: 'Last Modified',
      width: 120,
      type: 'dateTime',
      valueGetter: formatDate,
      renderCell: renderCellExpand
    },
    {
      field: archLevel === 'site' ? 'emr_publish_version_isf' : 'tmf_publish_version',
      headerName: 'Version',
      width: 90,
      renderCell: (params: any) => logVersions(params.row)
    }
  ];
  const logVersions = (row: any) => {
    const version = archLevel === 'site' ? row.emr_publish_version_isf : row.tmf_publish_version;
    const retunVal =
      version !== null && version !== `1.0` ? (
        <Link onClick={field => handleVersionHistory(row)}>
          {version === 1 ? version.toFixed(1) : Number(version) > 0.9 ? Number(version).toFixed(1).toString() : version}
        </Link>
      ) : (
        Number(1.0).toFixed(1).toString()
      );
    return retunVal;
  };

  const handelBack = () => {
    setStudyLogOpen(true);
    setEMREvaluationOpen(false);
  };
  useEffect(() => {
    if (pageSize === 10 || pageSize === 20 || pageSize === 30) {
      if (pageNumber * pageSize > rowsCount - 1) {
        setPageNumber(0);
        getAllEMREvaluations(0, pageSize);
      } else {
        getAllEMREvaluations(pageNumber * pageSize, pageSize);
      }
    }
  }, [pageNumber, pageSize, studyId, selectedCountry, selectedSite, currentHierarchyLevel, filterObject]);
  const getAllEMREvaluations = (from: number, size: number) => {
    getAllEMREvaluation({
      data: filterObject,
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: archLevel == 'sponsor' ? process.env.REACT_APP_NAME : process.env.REACT_APP_NAME_TEMP,
      from,
      size
    });
  };
  useEffect(() => {
    if (selectedSite === null) {
      handelBack();
    }
  }, [studyId, selectedCountry, selectedSite]);
  useEffect(() => {
    if (getAllEMREvaluationData && getAllEMREvaluationIsSuccess) {
      const tempData: any = getAllEMREvaluationData?.EMREvents.map((ele: any) => {
        return {
          ...ele,
          emr_publish_version_isf: ele.emr_publish_version_isf ? ele.emr_publish_version_isf : `1.0`,
          tmf_publish_version: ele.tmf_publish_version ? ele.tmf_publish_version : `1.0`
        };
      });
      setRowsCount(getAllEMREvaluationData?.total_records);
      setRows(tempData);
    } else {
      setRowsCount(0);
      setPageNumber(0);
      setRows([]);
    }
  }, [getAllEMREvaluationData, getAllEMREvaluationIsSuccess]);
  const handleVersionHistory = async (selectedRow: any) => {
    setHedertitle(selectedRow.heading);
    archLevel === 'site' ? setDocId(selectedRow?.tp_document_id_isf) : setDocId(selectedRow?.tmf_document_id);
    setShowVersionHistory(true);
  };
  useEffect(() => {
    if (selectedEMRRow != null) {
      setOpenEMRevaluation(true);
      setEMRGrid(false);
      dispatch(setSelectedEMRId(selectedEMRRow?.id));
    }
  }, [selectedEMRRow]);
  const handleClickEmREvalution = (row: any) => {
    if (!isHierarchyLevelClosed) {
      setselectedEMRRow(row);
    } else {
      dispatch(setMessage({ msgDetails: 'Please select the site', severity: 'error' }));
    }
  };
  const filterValues = (data: any) => {
    setFilterObject(data);
  };
  const definedCustomDatGridExport = (exportFields: any = {}) => {
    return (
      <GridToolbarExport
        csvOptions={{
          fileName: `EMR Evaluation Questionnaire ${moment(new Date()).format(
            `${(userPreferedDateFormat.includes('/')
              ? userPreferedDateFormat.replaceAll('/', '_')
              : userPreferedDateFormat.replaceAll('-', '_')
            ).toUpperCase()} hh_mm A`
          )}`
        }}
      />
    );
  };
  const dataTableProps = {
    filterMode: 'server'
  };
  return (
    <div>
      {viewEMRGrid ? (
        <>
          <div>
            <Box className="mainBackBtnWrap" sx={{ mt: -1}}>
              <Button onClick={() => handelBack()} startIcon={<ArrowBackIcon fontSize="small" />}>
                LIST OF LOGS
              </Button>
            </Box>
          </div>
          <DataTable
            hasDefinedColumns
            columns={columns}
            rows={rows}
            loading={getAllEMREvaluationIsLoading}
            datePreference={userPreferedDateFormat}
            paging
            rowsCount={rowsCount}
            pageNumber={pageNumber}
            pageSize={pageSize}
            setPageSize={setPageSize}
            setPageNumber={setPageNumber}
            paginationMode="server"
            className="studylogDatagrid"
            exportAll
            height="74vh"
            hasDefinedDataGridToolbarExport
            definedGridToolbarExport={definedCustomDatGridExport}
            filterValues={(data: any) => filterValues(data)}
            dataTableProps={dataTableProps}
          />
        </>
      ) : null}

      {openEMRevaluation ? (
        <EMREvaluationQuestionnaire
          selectedEMRRow={selectedEMRRow}
          setEMRGrid={setEMRGrid}
          setOpenEMRevaluation={setOpenEMRevaluation}
          setselectedEMRRow={setselectedEMRRow}
          getAllEMREvaluations={getAllEMREvaluations}
          pageNumber={pageNumber}
          pageSize={pageSize}
        />
      ) : (
        ''
      )}
      {showVersionHistory ? (
        <VersionHistory
          openVersoinHistory={showVersionHistory}
          setOpenVersoinHistory={setShowVersionHistory}
          title={headerTitle}
          documentId={docId}
          auditModuleSection={'EMR Evaluation Questionnaire'}
        />
      ) : (
        ''
      )}
    </div>
  );
};
export default DataGridEMREvaluation;
